import React from 'react';
import Chip, { type ChipProps } from '@mui/material/Chip';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import ConstituentType from '@/constants/ConstituentType';
import FilterOperator from '@/constants/forms/FilterOperator';
import OperatorLabelMap from '@/constants/forms/OperatorLabelMap';
import ConstituentIcons from '@/lib/configs/ConstituentIcons';
import {
  DONATION_RECURRING_CADENCES,
  DONATION_TYPES,
} from '@/lib/configs/contributions';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import translate from '@/lib/intl/translate';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import { formatCurrency } from '@/utils/currency';
import { convertMonthNumberToText } from '@/utils/dates';
import isConstituentId from '@/utils/isConstituentID';
import DateTypeOptions from '../../constants/DateTypeOptions';
import FieldLabelMap from '../../constants/FieldLabelMap';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import BoardValue from './BoardValue';
import CampaignValue from './CampaignValue';
import ColumnValue from './ColumnValue';
import ConstituentValue from './ConstituentValue';
import LabelValue from './LabelValue';
import UserValue from './UserValue';

type FilterChipProps = AdvancedConstituentSearchFilter &
  Pick<ChipProps, 'onDelete' | 'disabled'> & {
    isAdvanced?: boolean;
  };

function FilterChip({
  field,
  isAdvanced = false,
  isCustomField,
  onDelete,
  operator,
  value = '',
  disabled = false,
}: FilterChipProps) {
  const { data: customFields } = useCustomFields();

  const displayField: string | undefined = React.useMemo(() => {
    let result;
    if (field in FieldLabelMap) {
      result = translate(FieldLabelMap[field as ConstituentSearchFilterField]);
    }
    if (isCustomField && customFields) {
      result = customFields.find(({ customFieldId }) => customFieldId === field)
        ?.fieldName;
    }
    return result;
  }, [customFields, isCustomField, field]);

  const displayValue: React.ReactNode | string | null = React.useMemo(() => {
    if (
      operator === FilterOperator.IS_NOT_NULL ||
      operator === FilterOperator.IS_NULL
    ) {
      return '';
    }
    let result = null;

    switch (field) {
      case ConstituentSearchFilterField.DONATION_TYPES:
        if (Array.isArray(value)) {
          result = value
            .map(
              (item) =>
                DONATION_TYPES[
                  item.toUpperCase() as keyof typeof DONATION_TYPES
                ].displayText,
            )
            .join(', ');
        }
        break;
      case ConstituentSearchFilterField.BIRTHDAY_MONTH:
        if (typeof value === 'number') {
          result = convertMonthNumberToText(value);
        }
        break;
      case ConstituentSearchFilterField.CONSTITUENT_CATEGORIES:
      case ConstituentSearchFilterField.CONSTITUENT_RULES:
        if (Array.isArray(value)) {
          result = value
            .reduce((arr, val) => {
              const match = ConstituentIcons.find((item) => item.value === val);
              return match ? [...arr, match.label as string] : arr;
            }, [])
            .join(', ');
        }
        break;
      case ConstituentSearchFilterField.CONSTITUENT_TYPE:
        result = translate(
          ConstituentType.PERSON === value
            ? 'FORMS_DONOR_TYPE_OPTION_INDIVIDUAL'
            : 'FORMS_DONOR_TYPE_OPTION_ORGANIZATION',
        );
        break;
      case ConstituentSearchFilterField.BOARD_IDS:
        result = <BoardValue value={value as string[]} />;
        break;
      case ConstituentSearchFilterField.COLUMN_IDS:
        result = <ColumnValue value={value as string[]} />;
        break;
      case ConstituentSearchFilterField.IN_HONOR:
        result = isConstituentId(String(value)) ? (
          <ConstituentValue value={String(value)} />
        ) : (
          String(value)
        );
        break;
      case ConstituentSearchFilterField.CONNECTION_CONSTITUENT_IDS:
      case ConstituentSearchFilterField.CONNECTION_EMPLOYER_CONSTITUENT_IDS:
      case ConstituentSearchFilterField.CONNECTION_HOUSEHOLD_CONSTITUENT_IDS:
        result = <ConstituentValue value={value as string} />;
        break;
      case ConstituentSearchFilterField.USER_IDS:
        result = <UserValue value={value as string | string[]} />;
        break;
      case ConstituentSearchFilterField.CONSTITUENT_LABEL_IDS:
      case ConstituentSearchFilterField.DONATION_LABEL_IDS:
        result = <LabelValue value={value as string[]} />;
        break;
      case ConstituentSearchFilterField.DONATION_AMOUNT:
      case ConstituentSearchFilterField.DONATION_LIFETIME_TOTAL_AMOUNT:
      case ConstituentSearchFilterField.DONATION_THIS_YEAR_TOTAL_AMOUNT:
      case ConstituentSearchFilterField.DONATION_LAST_YEAR_TOTAL_AMOUNT:
        if (typeof value === 'number') {
          result = formatCurrency(value, true);
        } else if (Array.isArray(value)) {
          result = (value as number[])
            .map((item) => formatCurrency(item, true))
            .join('-');
        }
        break;
      case ConstituentSearchFilterField.DONATION_MINUTES:
      case ConstituentSearchFilterField.DONATION_LIFETIME_TOTAL_TIME:
      case ConstituentSearchFilterField.DONATION_THIS_YEAR_TOTAL_TIME:
      case ConstituentSearchFilterField.DONATION_LAST_YEAR_TOTAL_TIME:
        if (typeof value === 'number') {
          result = translate('X_HOURS', { hours: value / 60 });
        } else if (Array.isArray(value)) {
          result = (value as number[])
            .map((item) => translate('X_HOURS', { hours: item / 60 }))
            .join('-');
        }
        break;
      case ConstituentSearchFilterField.DONATION_DATE_TYPE:
        result =
          DateTypeOptions.find((item) => item.value === value)?.label || null;
        break;
      case ConstituentSearchFilterField.CAMPAIGN_IDS:
        result = <CampaignValue value={value as string[]} />;
        break;
      case ConstituentSearchFilterField.DONATION_PLAN_CADENCE:
        result =
          DONATION_RECURRING_CADENCES.find((item) => item.value === value)
            ?.text || null;
        break;
      default:
        result = Array.isArray(value) ? value.join(', ') : String(value);
        break;
    }
    return result;
  }, [customFields, isCustomField, field, value]);

  const displayOperator = React.useMemo(() => {
    if (
      isCustomField &&
      (operator === FilterOperator.IS_NULL ||
        operator === FilterOperator.IS_NOT_NULL)
    ) {
      // custom field checkboxes are either checked or null - there is no false
      // state. We query custom field checkboxes by checking for "is_null" or
      // "is_not_null", but for basic searching, we still want to present it to
      // the user as true/false
      return String(operator === FilterOperator.IS_NOT_NULL);
    }
    return isAdvanced ||
      (operator !== FilterOperator.IS && operator !== FilterOperator.ANY)
      ? `${translate(OperatorLabelMap[operator])}${displayValue ? ': ' : ''}`
      : '';
  }, [displayValue, isAdvanced, isCustomField, operator]);

  const label =
    displayField && displayValue !== null ? (
      <span>
        <b>{displayField}:</b> {displayOperator}
        {displayValue}
      </span>
    ) : null;

  return label ? (
    <Chip
      label={label}
      onDelete={onDelete}
      className="max-w-none md:max-w-full"
      disabled={disabled}
    />
  ) : null;
}

export default FilterChip;
